import { useSelector, shallowEqual } from '@xstate/react'
import clsx from 'clsx'
import { TargetedEvent, useCallback } from 'preact/compat'

import { fromCurrency, toCurrency } from '../lib/currency'
import { Interactions, trackingService } from '../machines/tracking.machine'
import { PaymentFrequencyValues, SelectOptions } from '../types/global'
import { AnimatedNumber } from './AnimatedNumber'
import { SolidPencilIcon } from './icons/SolidPencil'
import { costInputsSelector, useNaturalPowerContext } from './NaturalPowerCalculatorProvider'
import { Select } from './Select'
import { Tile } from './Tile'
import { Tooltip } from './Tooltip'

interface CostInputsProps {
  className?: string
  paymentFrequencyOptions: SelectOptions<PaymentFrequencyValues>

  paymentFrequencyLabel?: string
  currentCostLabel?: string
  estimatedCostLabel?: string
  estimatedNaturalPowerCostLabel?: string
}

export function CostInputs({
  className,
  paymentFrequencyOptions,
  paymentFrequencyLabel,
  currentCostLabel,
  estimatedCostLabel,
  estimatedNaturalPowerCostLabel,
}: CostInputsProps) {
  const services = useNaturalPowerContext()

  const { paymentFrequency, currentCost, estimatedPremium, estimatedTotal } = useSelector(
    services.costInput,
    costInputsSelector,
    shallowEqual,
  )

  const getCost = useCallback(
    (value: string) => {
      const newCost = fromCurrency(value)
      return isNaN(newCost) ? currentCost : newCost
    },
    [currentCost],
  )

  const handleChangeCost = useCallback(
    (e: TargetedEvent<HTMLInputElement>) => {
      const newCost = getCost(e.currentTarget.value)
      e.currentTarget.value = toCurrency(newCost)
      if (newCost !== currentCost) {
        services.costInput.send({ type: 'Update inputs', currentCost: newCost.toString() })
      }
    },
    [currentCost, getCost, services.costInput],
  )

  const handleTrackCost = () => {
    trackingService.send({ type: 'Interact', name: Interactions.currentCost })
  }

  const handleChangeFrequency = (paymentFrequency: PaymentFrequencyValues) => {
    services.costInput.send({
      type: 'Update inputs',
      paymentFrequency: Number(paymentFrequency) as PaymentFrequencyValues,
    })
    trackingService.send({ type: 'Interact', name: Interactions.paymentFrequency })
  }

  return (
    <div className={clsx('grid grid-cols-2 gap-4', className)}>
      <Tile
        label={paymentFrequencyLabel}
        className="h-32 focus-within:z-20"
        inputId="paymentFrequency"
        tooltip={
          <Tooltip id="paymentFrequency-tooltip" title="Payment frequency">
            This is how often your business is billed.
          </Tooltip>
        }>
        <Select
          id="paymentFrequency"
          aria-describedby="paymentFrequency-tooltip"
          options={paymentFrequencyOptions}
          value={paymentFrequency}
          onChange={handleChangeFrequency}
        />
      </Tile>
      <Tile
        label={currentCostLabel}
        className="h-32 focus-within:z-20"
        inputId="currentCost"
        tooltip={
          <Tooltip title={currentCostLabel} id="currentCost-tooltip">
            This is your current average bill amount. For a more accurate view, login to your MyAccount, check your
            latest bill or speak to your Account Manager.
          </Tooltip>
        }>
        <div className="flex items-center w-full border-b h-7">
          <input
            type="text"
            min={0}
            name="currentCost"
            id="currentCost"
            aria-describedby="currentCost-tooltip"
            value={toCurrency(currentCost)}
            onChange={handleChangeCost}
            onBlur={handleTrackCost}
            className="block w-full min-h-0 p-0 font-sans text-xl bg-transparent border-none appearance-none h-7 focus:outline-none focus:border-none placeholder:transition-colors placeholder:text-white focus:placeholder:text-white/40"
            placeholder="0"
          />
          <SolidPencilIcon className="block w-4 h-4 pointer-events-none shrink-0" />
        </div>
      </Tile>
      <Tile
        label={estimatedCostLabel}
        className="h-32"
        footnoteId={3}
        tooltip={
          <Tooltip title={estimatedCostLabel}>
            Your NaturalPower contribution cost based on the % you have chosen to offset your traditional energy use.
            Use the slider to change your contribution if needed.
          </Tooltip>
        }>
        <AnimatedNumber value={estimatedPremium} transform={toCurrency} />
      </Tile>
      <Tile
        label={estimatedNaturalPowerCostLabel}
        className="h-32"
        tooltip={
          <Tooltip title={estimatedNaturalPowerCostLabel}>
            This is your total bill cost, including your NaturalPower contribution based on the % you have chosen to
            offset your traditional energy use (25%, 50%, 75% and 100%).
          </Tooltip>
        }>
        <div className="max-w-full truncate">
          <AnimatedNumber value={estimatedTotal} transform={toCurrency} />
        </div>
      </Tile>
    </div>
  )
}
